import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getClientId } from 'utils';
import { getAdvisoryValidTillDate } from 'utils/advisoty';
import { JAUsersType } from 'datasource/useApplicationDetails/ApplicationDetailsManager.types';
import { dateFormatZurich, formatDate } from 'utils/datetime';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import { getSetupData } from 'additiv-services/setup';

const setupData = getSetupData();

export const useCommonApplicationDetails = ({
    investmentAppsData,
    is3aVb = undefined,
}: {investmentAppsData?: any[], is3aVb: boolean | undefined}) => {
    const { containerId, dfsClientId } = useParams();
    const { i18n: { language } } = useTranslation();

    const [hasMounted, setHasMounted] = useState(false);

    const contactId = getClientId(dfsClientId);
    const applicationID = useMemo(() => investmentAppsData?.[0]?.investmentApplicationId, [investmentAppsData?.[0]?.investmentApplicationId]);

    const advisoryDocId = useMemo(() => investmentAppsData?.find(appData => appData?.investmentDescription?.advisoryDocumentId)?.investmentDescription?.advisoryDocumentId, [investmentAppsData]);
    const isAdvisoryCase = !!advisoryDocId;

    const {
        data: advisoryDetailsDataRaw,
        isLoading: isAdvisoryDataLoading,
        refetch: refetchAdvisory,
    } = useGetCustomService({
        service: 'customAdvisoryServices',
        api: 'getAdvisory',
        payload: { id: advisoryDocId },
        enabled: !!advisoryDocId,
        refetchOnWindowFocus: false,
    });

    const advisoryDetailsData = advisoryDetailsDataRaw?.data;

    const isOldApplicationWithAdvisory = useMemo(() => advisoryDetailsData?.status === 1 && advisoryDetailsData?.metadata === null, [advisoryDetailsData?.status, advisoryDetailsData?.metadata]);
    const isNonAdvisoryOldApplicationWithContainerId = useMemo(() => investmentAppsData?.some(application => application?.investmentDescription?.kycDone === false), [investmentAppsData]);
    const showGeneralDocumentsAndProofOfAssetsSection = useMemo(() => !isOldApplicationWithAdvisory && !isNonAdvisoryOldApplicationWithContainerId && !!containerId, [isOldApplicationWithAdvisory, isNonAdvisoryOldApplicationWithContainerId, containerId]);

    const {
        data: documentsByContainerRaw,
        isLoading: isNon3bApplicationDocsLoading,
    } = useGetCustomService({
        service: 'customDocumentsService',
        api: 'getDocumentsByContainer',
        payload: { containerId, language },
        enabled: is3aVb === false && !!containerId && !setupData?.UPLOAD_STATIC_DOCUMENT_FEATURE_FLAG,
        refetchOnWindowFocus: false,
        staleTime: 3 * 1000, // 3 seconds
        gcTime: 3 * 1000, // 3 seconds
    });

    const {
        data: documentsByContainerRawWithNewAPI,
        isLoading: isNon3bApplicationDocsLoadingWithNewAPI,
    } = useGetCustomService({
        service: 'customDocumentsService',
        api: 'getDocumentsByContainerWithNewAPI',
        payload: { contactId, containerId, language },
        enabled: is3aVb === false && !!containerId && setupData?.UPLOAD_STATIC_DOCUMENT_FEATURE_FLAG,
        refetchOnWindowFocus: false,
        staleTime: 3 * 1000, // 3 seconds
        gcTime: 3 * 1000, // 3 seconds
    });
    const applicationsDocumentsData = documentsByContainerRawWithNewAPI?.data?.documents || documentsByContainerRaw?.data?.documents;

    const {
        data: oldApplicationsDocumentsRaw,
        isLoading: isOldApplicationDocsLoading,
    } = useGetCustomService({
        service: 'customDocumentsService',
        api: 'getDocumentsByInvestmentApplicationId',
        payload: { investmentApplicationId: applicationID, contactId, language },
        enabled: !!applicationID && !!contactId && is3aVb === false && !containerId && !setupData?.UPLOAD_STATIC_DOCUMENT_FEATURE_FLAG,
        refetchOnWindowFocus: false,
        staleTime: 3 * 1000, // 3 seconds
        gcTime: 3 * 1000, // 3 seconds
    });

    const {
        data: oldApplicationsDocumentsRawNew,
        isLoading: isOldApplicationDocsLoadingNew,
    } = useGetCustomService({
        service: 'customDocumentsService',
        api: 'getDocumentsByInvestmentApplicationIdWithNewAPI',
        payload: { investmentApplicationId: applicationID, contactId, language },
        enabled: !!applicationID && !!contactId && is3aVb === false && !containerId && setupData?.UPLOAD_STATIC_DOCUMENT_FEATURE_FLAG,
        refetchOnWindowFocus: false,
        staleTime: 3 * 1000, // 3 seconds
        gcTime: 3 * 1000, // 3 seconds
    });
    const oldApplicationsDocumentsData = setupData?.UPLOAD_STATIC_DOCUMENT_FEATURE_FLAG ? oldApplicationsDocumentsRawNew?.data?.documents : oldApplicationsDocumentsRaw?.data;

    const {
        data: documentDataForOldAdvisoryRaw,
        isLoading: isDocumentDataForOldAdvisoryLoading,
    } = useGetCustomService({
        service: 'customDocumentsService',
        api: 'getAdvisoryDocument',
        payload: { advisoryDocumentId: advisoryDocId },
        enabled: !!advisoryDocId && isOldApplicationWithAdvisory && is3aVb === false,
        refetchOnWindowFocus: false,
        staleTime: 3 * 1000, // 3 seconds
        gcTime: 3 * 1000, // 3 seconds
    });
    const documentDataForOldAdvisory = documentDataForOldAdvisoryRaw?.data;

    const {
        data: generalDocumentsRaw,
        isLoading: isGetGeneralDocumentsLoading,
    } = useGetCustomService({
        service: 'customDocumentsService',
        api: 'getGeneralDocumentsAdvisoryWay',
        payload: { containerId, language },
        enabled: !!containerId && !!language
            && showGeneralDocumentsAndProofOfAssetsSection && hasMounted && is3aVb === false,
        refetchOnWindowFocus: false,
        staleTime: 3 * 1000, // 3 seconds
        gcTime: 3 * 1000, // 3 seconds
    });
    const generalDocumentsData = generalDocumentsRaw?.data;

    const {
        data: non3bApplicationDocumentRaw,
        isLoading: isApplicationDocsLoading,
        refetch: refetchNon3bApplicationsDocuments,
    } = useGetCustomService({
        service: 'customDocumentsService',
        api: 'getDocumentsByInvestmentApplicationId',
        payload: { investmentApplicationId: applicationID, contactId, language },
        enabled: !!applicationID && !!contactId && !!is3aVb && !setupData?.UPLOAD_STATIC_DOCUMENT_FEATURE_FLAG,
        refetchOnWindowFocus: false,
        staleTime: 3 * 1000, // 3 seconds
        gcTime: 3 * 1000, // 3 seconds
    });

    const {
        data: non3bApplicationDocumentRawNew,
        isLoading: isApplicationDocsLoadingNew,
        refetch: refetchNon3bApplicationsDocumentsNew,
    } = useGetCustomService({
        service: 'customDocumentsService',
        api: 'getDocumentsByInvestmentApplicationIdWithNewAPI',
        payload: { investmentApplicationId: applicationID, contactId, language },
        enabled: !!applicationID && !!contactId && !!is3aVb && setupData?.UPLOAD_STATIC_DOCUMENT_FEATURE_FLAG,
        refetchOnWindowFocus: false,
        staleTime: 3 * 1000, // 3 seconds
        gcTime: 3 * 1000, // 3 seconds
    });

    const non3bApplicationDocument = setupData?.UPLOAD_STATIC_DOCUMENT_FEATURE_FLAG ? non3bApplicationDocumentRawNew?.data?.documents : non3bApplicationDocumentRaw?.data;

    useEffect(() => {
        if (!isAdvisoryDataLoading && investmentAppsData) {
            setHasMounted(true);
        }
    }, [isAdvisoryDataLoading, investmentAppsData]);

    const contactGroupId = investmentAppsData?.[0]?.investmentDescription?.contactGroupId;
    const isJA = investmentAppsData?.[0]?.isJointAccount;
    const jaUsers:JAUsersType = useMemo(() => {
        const owners = investmentAppsData?.[0]?.owners;

        return owners ? Object?.keys(owners)?.map((key) => ({
            id: parseInt(key, 10),
            fullName: owners[key],
        })) : [];
    }, [isJA]);

    const {
        data: proofOfAssetsDocsRaw,
        isLoading: isProofOfAssetsDocsDataLoading,
    } = useGetCustomService({
        service: 'customClientService',
        api: 'getProofOfAssetsDocs',
        payload: { contactGroupId, language, containerId },
        enabled: !!contactGroupId && !!language && !!containerId
            && showGeneralDocumentsAndProofOfAssetsSection && hasMounted && is3aVb === false,
        refetchOnWindowFocus: false,
    });
    const proofOfAssetsDocsData = proofOfAssetsDocsRaw?.data;

    const advisoryData = useMemo(() => {
        const sumOfPlannedInvestmentAmounts = investmentAppsData?.reduce(
            (akku, item) => {
                const changeInvestmentAmount = item?.investmentDescription?.changeInvestmentAmount;

                if (item?.changePlan) {
                    if (changeInvestmentAmount) {
                        const changeValue = changeInvestmentAmount.value ?? 0;

                        return changeInvestmentAmount.isSell ? (akku - changeValue) : (akku + changeValue);
                    }

                    return akku;
                }

                return akku + (item?.plannedInvestmentAmount ?? 0);
            },
            0,
        );

        return {
            advisoryNo: advisoryDetailsData?.advisoryNumber ?? '-',
            createdAt: advisoryDetailsData?.date ? formatDate(advisoryDetailsData?.date, dateFormatZurich) : '-',
            validTill: advisoryDetailsData?.date ? getAdvisoryValidTillDate({ advisoryDate: advisoryDetailsData?.date, system: advisoryDetailsData?.system }) : '-',
            investmentAmount: sumOfPlannedInvestmentAmounts,
        };
    }, [
        investmentAppsData, advisoryDetailsData?.advisoryNumber,
        advisoryDetailsData?.date,
        advisoryDetailsData?.system,
    ]);

    return {
        isJA: is3aVb ? false : isJA,
        jaUsers: is3aVb ? undefined : jaUsers,
        isApplicationDocsLoading: isApplicationDocsLoading || isNon3bApplicationDocsLoading || isOldApplicationDocsLoading || isOldApplicationDocsLoadingNew || isApplicationDocsLoadingNew || isNon3bApplicationDocsLoadingWithNewAPI,
        isGetGeneralDocumentsLoading,
        isProofOfAssetsDocsDataLoading,
        isAdvisoryCase,
        generalDocumentsData: is3aVb ? undefined : generalDocumentsData,
        contactGroupId: is3aVb ? undefined : contactGroupId,
        proofOfAssetsDocsData: is3aVb ? undefined : proofOfAssetsDocsData,
        advisoryDocId: is3aVb ? undefined : advisoryDocId,
        advisoryDetailsData: is3aVb ? undefined : advisoryDetailsData,
        advisoryData: is3aVb ? undefined : advisoryData,
        applicationsDocumentsData: applicationsDocumentsData || non3bApplicationDocument || oldApplicationsDocumentsData,
        documentDataForOldAdvisory,
        isDocumentDataForOldAdvisoryLoading,
        showGeneralDocumentsAndProofOfAssetsSection,
        refetchNon3bApplicationsDocuments: setupData?.UPLOAD_STATIC_DOCUMENT_FEATURE_FLAG ? refetchNon3bApplicationsDocumentsNew : refetchNon3bApplicationsDocuments,
        refetchAdvisory,
    };
};
